import { toast } from 'react-toastify';
import { uploadTrayReferenceImageForDraft } from 'api/Endpoints/SetEndpoint';
import { SetDraftResponse, SetTrayResponse } from 'api/Responses/SetResponses';
import { useLanguage } from 'context/AppContext';
import { ImageService } from 'services/FileService';
import { getFirebaseFile } from 'utils/helpers/googleStorage.helpers';
import {
  dataUriToFile,
  getGeneratedTempTrayRefImageFromFirebase,
  uploadAndSaveImage,
} from 'utils/helpers/image.helpers';

interface UseUploadTrayReferenceImage {
  addNewTray: () => Promise<SetTrayResponse>;
  onSaveTaggedImageToFirebase: (dataUri: string) => Promise<string>;
  onSaveTaggedImageToDraft: (dataUri: string) => Promise<string>;
}

const useUploadTrayReferenceImage = (set: SetDraftResponse, tray: SetTrayResponse): UseUploadTrayReferenceImage => {
  const [language] = useLanguage();

  const addNewTray = async (): Promise<SetTrayResponse> => {
    if (!tray.image_file) {
      toast.error(language.AddModal.Tray.noImageError);
      throw new Error(language.AddModal.Tray.noImageError);
    }

    if (tray.reference.length <= 0) {
      toast.error(language.AddModal.Tray.referenceWarning);
      throw new Error(language.AddModal.Tray.referenceWarning);
    }

    if (!tray.name) {
      toast.error(language.AddModal.Tray.noNameError);
      throw new Error(language.AddModal.Tray.noNameError);
    }

    // get temp image saved in firebase and post it to the DRAFT bucket (there is a dependency on the tray reference already existing)
    const firebaseImageUrl = await ImageService.getImage(tray.image_file, false);
    const imageFile = await getFirebaseFile(firebaseImageUrl);
    const imageResponse = await uploadTrayReferenceImageForDraft(set.code, imageFile);
    const updatedSet = await uploadAndSaveImage(set, tray.reference, imageResponse.image_path, tray);

    // delete the temp image from firebase
    await ImageService.deleteImage(tray.image_file);

    const newTray = updatedSet.trays[tray.reference];

    return newTray;
  };

  const onSaveTaggedImageToFirebase = async (dataUri: string): Promise<string> => {
    const imageFile = dataUriToFile(dataUri, 'tagged_reference_image.png');
    const imagePath = getGeneratedTempTrayRefImageFromFirebase(imageFile.name);
    // Upload image to firebase
    await ImageService.saveImage(imagePath, imageFile);
    return imagePath;
  };

  const onSaveTaggedImageToDraft = async (dataUri: string): Promise<string> => {
    const imageFile = dataUriToFile(dataUri, 'tagged_reference_image.png');
    const imageResponse = await uploadTrayReferenceImageForDraft(set.code, imageFile);

    await uploadAndSaveImage(set, tray.reference, imageResponse.image_path);

    return imageResponse.image_path;
  };

  return { addNewTray, onSaveTaggedImageToFirebase, onSaveTaggedImageToDraft };
};

export default useUploadTrayReferenceImage;
